import { Alert, Box, Button, Collapse, Paper, Stack, Typography } from '@mui/material';
import { isNil } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMixpanel } from 'react-mixpanel-browser';
import { useNavigate } from 'react-router-dom';

import { useCustomerInfo } from '@/api/customers';
import { usePostRegisterDevice } from '@/api/device';
import { useSubscriptionsInfo } from '@/api/subscriptions';
import { queryClient } from '@/common/react-query';
import { RegistrationResultDialog } from '@/components/RegistrationResultDialog';
import { RegistrationResultDialogContent } from '@/components/RegistrationResultDialogContent';
import { FormInputText } from '@/components/form/FormInputText';
import { LoadingIndicatorIcon } from '@/components/loaders/LoadingIndicatorIcon';
import { STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY, SUBSCRIPTION_TAGS_INFO_QUERY_KEY } from '@/config';
import { useDisclosure } from '@/hooks/useDisclosure';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';

export const SpotterRegister = () => {
    const navigate = useNavigate();
    const spotterIdInputRef = useRef<HTMLInputElement>(null);
    const codeInputRef = useRef<HTMLInputElement>(null);
    const { isOpen: isSuccessDialogOpen, open: openSuccessDialog, close: closeConfirmDialog } = useDisclosure();
    const mixpanel = useMixpanel();
    const { data: subscriptionInfo } = useSubscriptionsInfo();
    const { data: customerInfo } = useCustomerInfo();
    const preRegistrationBalanceCentsRef = useRef<number | undefined>(undefined);
    const [hadNoPriorSubscription, setHadNoPriorSubscription] = useState<boolean>();

    const {
        mutateAsync: postRegisterDevice,
        isError,
        isPending,
        error,
        data: registerDeviceResponse,
    } = usePostRegisterDevice();
    const {
        control,
        handleSubmit,
        getValues,
        reset: resetForm,
        formState,
    } = useForm<{ spotterId: string; activationCode: string }>({
        defaultValues: {
            spotterId: '',
            activationCode: '',
        },
        mode: 'onChange',
    });

    useUnsavedChangesGuard(formState.isDirty, 'spotter-register', {
        text: `You have not yet completed your Spotter's registration.`,
    });

    useEffect(() => {
        // Effect: Once subscriptionInfo is loaded, note whether we have any subscriptions, but
        // don't update it because we don't want to think the one that just got created already existed.
        if (!isNil(hadNoPriorSubscription)) {
            return;
        }
        if (!subscriptionInfo) {
            return;
        }

        setHadNoPriorSubscription(!subscriptionInfo.data.length);
    }, [subscriptionInfo, hadNoPriorSubscription]);

    const handleCloseSuccessDialog = () => {
        setHadNoPriorSubscription(false);
        closeConfirmDialog();
    };

    const onSpotterRegister = useCallback(
        async (data: { spotterId: string; activationCode: string }) => {
            preRegistrationBalanceCentsRef.current = customerInfo?.data?.balance;

            try {
                await postRegisterDevice({
                    spotterId: data.spotterId.trim(),
                    activationCode: data.activationCode.trim(),
                });

                resetForm();
                openSuccessDialog();
                queryClient.invalidateQueries({ queryKey: ['stripe-subscription-info'] });
                queryClient.invalidateQueries({ queryKey: ['user-credit-balance'] });
                queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_TAGS_INFO_QUERY_KEY] });
                queryClient.invalidateQueries({ queryKey: [STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY] });
                queryClient.invalidateQueries({ queryKey: ['user-account'] });
                queryClient.invalidateQueries({ queryKey: ['stripe-invoices-list'] });
                queryClient.invalidateQueries({ queryKey: ['device-id-list'] });
                mixpanel?.track('Register Spotter Success', {
                    spotterId: data.spotterId,
                });
            } catch (err) {
                mixpanel?.track('Register Spotter Error', {
                    spotterId: data.spotterId,
                    message: (err as { message: string })?.message,
                });
                console.error('error while attempting to register Spotter', err);
            }
        },
        [openSuccessDialog, postRegisterDevice, resetForm, mixpanel, customerInfo]
    );

    useEffect(() => {
        const keyPressListener = async (event: KeyboardEvent) => {
            if (event.key === 'Enter' && !isPending) {
                const [spotterId, activationCode] = getValues(['spotterId', 'activationCode']);
                if (!spotterId) {
                    spotterIdInputRef.current?.focus();
                } else if (!activationCode) {
                    codeInputRef.current?.focus();
                } else {
                    handleSubmit(onSpotterRegister)();
                }
            }
        };
        window.addEventListener('keypress', keyPressListener);
        return () => {
            window.removeEventListener('keypress', keyPressListener);
        };
    }, [getValues, handleSubmit, isPending, onSpotterRegister]);

    return (
        <Paper sx={{ p: 4 }} elevation={1}>
            <Stack gap={1}>
                <Typography variant="h4">Enter your Spotter ID and activation code</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                    <FormInputText
                        name="spotterId"
                        control={control}
                        label="Spotter ID"
                        inputFieldProps={{
                            inputRef: spotterIdInputRef,
                            autoFocus: true,
                            type: 'text',
                            id: 'spotterId',
                            required: true,
                            placeholder: 'e.g. SPOT-23456C',
                            size: 'medium',
                            helperText: 'Your Spotter ID can be found on the top of your Spotter',
                        }}
                        controllerProps={{
                            rules: {},
                        }}
                    />
                    <FormInputText
                        name="activationCode"
                        control={control}
                        label="Activation code"
                        inputFieldProps={{
                            inputRef: codeInputRef,
                            type: 'text',
                            id: 'activationCode',
                            required: true,
                            placeholder: 'e.g. 1234567890',
                            size: 'medium',
                            helperText:
                                'Your activation code is in the box your Spotter was shipped in as well as sent to your email',
                        }}
                        controllerProps={{
                            rules: {
                                pattern: {
                                    value: /^[0-9]+$/,
                                    message: 'Activation code must be a number',
                                },
                            },
                        }}
                    />
                </Stack>
                <Collapse in={isError} data-testid="register-a-spotter-error-container">
                    <Alert sx={{ my: 1 }} severity="error" data-testid="register-a-spotter-error-message">{`Error! ${
                        error?.message ?? ''
                    } ${(error?.response?.data as { error: string })?.error ?? ''}`}</Alert>
                </Collapse>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        sx={{ textWrap: 'nowrap' }}
                        onClick={handleSubmit(onSpotterRegister)}
                        disabled={isPending}
                        startIcon={<LoadingIndicatorIcon isLoading={isPending} color="info" />}
                    >
                        Register
                    </Button>
                </Box>
            </Stack>
            <RegistrationResultDialog
                isOpen={isSuccessDialogOpen}
                onReject={handleCloseSuccessDialog}
                onConfirm={() => navigate('/telemetry')}
                title="Registration successful"
                confirmButtonLabel="View telemetry"
                rejectButtonLabel="Register another Spotter"
            >
                <RegistrationResultDialogContent
                    registerDeviceResponse={registerDeviceResponse?.data}
                    hadNoPriorSubscription={hadNoPriorSubscription}
                    preRegistrationBalanceCents={preRegistrationBalanceCentsRef.current}
                />
            </RegistrationResultDialog>
        </Paper>
    );
};
